/* eslint-disable sonarjs/cognitive-complexity */
import React, { FC, useState } from 'react'
import classNames from 'classnames'
import { FlexRow } from '@/atoms/FlexContainers'
import { ArrowLeftIcon } from '@/atoms/Icons/ArrowLeftIcon'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { InternalLink } from '@/atoms/InternalLink'
import { AngelLogo } from '@/atoms/Logos/AngelLogo'
import { TitleLG } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { useThemeContext } from '@/contexts/ThemeContext'
import { LanguageMenu } from '@/molecules/LanguageSelector'
import { RegionSelector } from '@/molecules/RegionSelector'
import { UserMenu } from '@/organisms/SiteNav/UserMenu'
import { useUser } from '@/services/UserService'
import { TheatricalReleaseRegion } from '@/types/codegen-federation'
import { useTranslate } from '@/utils/translate/translate-client'

export type TicketNavVariantTypes = 'BACK' | 'CLOSE' | 'NONE'
export interface TicketNavProps {
  title?: string
  navigationVariant?: TicketNavVariantTypes
  onNavigationIconClick?: () => void
  navClassName?: string
  shouldHideOnMobile?: boolean
  shouldShowLanguageModal?: boolean
  regions?: TheatricalReleaseRegion[]
  useDefaultNav?: boolean
}

const TicketNav: FC<TicketNavProps> = ({
  title,
  navigationVariant = 'BACK',
  onNavigationIconClick,
  navClassName,
  shouldShowLanguageModal = false,
  regions,
  shouldHideOnMobile = false,
  useDefaultNav = false,
}) => {
  const [isRegionSelectorOpen, setIsRegionSelectorOpen] = useState(false)
  const { isDarkMode } = useThemeContext()
  const { t } = useTranslate('tickets')
  const { isLoggedIn } = useUser()

  const [isLanguagesOpen, setIsLanguagesOpen] = useState(false)
  const shouldShowBack = navigationVariant === 'BACK'
  const shouldShowClose = navigationVariant === 'CLOSE'
  const hasNavigationIcon = shouldShowBack || shouldShowClose
  const showDefaultMobileNav = useDefaultNav ? !isLoggedIn : true
  const defaultTitle = t('showtimes', 'Showtimes')

  const getDisplayTitle = (showDefaultMobileNav: boolean, title: string | undefined, defaultTitle: string) => {
    if (title) return title
    if (!showDefaultMobileNav) return defaultTitle
    return ''
  }

  return (
    <header
      className={classNames(
        useDefaultNav && (showDefaultMobileNav ? 'block' : 'hidden md:block'),
        !useDefaultNav && (shouldHideOnMobile ? 'hidden md:block' : 'block'),
      )}
    >
      <nav
        className={classNames(
          'sticky top-0 z-[51] flex h-16 items-center justify-between px-4',
          isDarkMode ? 'bg-core-gray-900' : 'bg-white',
          navClassName,
        )}
      >
        <FlexRow className="gap-2 sm:gap-3">
          {hasNavigationIcon ? (
            <button onClick={onNavigationIconClick}>
              {shouldShowBack && <ArrowLeftIcon color={isDarkMode ? 'white' : 'black'} />}
              {shouldShowClose && <CloseIcon color={isDarkMode ? 'white' : 'black'} size={24} />}
            </button>
          ) : (
            <InternalLink aria-label="Angel Studios" href={paths.home}>
              <AngelLogo color={isDarkMode ? 'white' : 'black'} />
            </InternalLink>
          )}
          <TitleLG
            className="relative text-base sm:absolute sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:text-lg md:text-2xl"
            color={isDarkMode ? 'white' : 'core-gray-950'}
            weight="bold"
          >
            {getDisplayTitle(showDefaultMobileNav, title, defaultTitle)}
          </TitleLG>
        </FlexRow>
        <FlexRow className="gap-3 sm:gap-6">
          {regions && regions?.length > 0 && (
            <RegionSelector
              onOpen={() => setIsRegionSelectorOpen(true)}
              onClose={() => setIsRegionSelectorOpen(false)}
              isOpen={isRegionSelectorOpen}
              onRegionModalOpen={() => setIsRegionSelectorOpen(true)}
              isDarkMode={isDarkMode}
              regions={regions}
              showDefaultMobileNav={showDefaultMobileNav}
            />
          )}
          {shouldShowLanguageModal && (
            <LanguageMenu
              isDarkMode={isDarkMode}
              onOpen={() => {
                setIsLanguagesOpen(true)
              }}
              isOpen={isLanguagesOpen}
              onClose={() => {
                setIsLanguagesOpen(false)
              }}
              showDefaultMobileNav={showDefaultMobileNav}
            />
          )}
          {hasNavigationIcon && (
            <InternalLink aria-label="Angel Studios" href={paths.home}>
              <AngelLogo height={22} color={isDarkMode ? 'white' : 'black'} />
            </InternalLink>
          )}

          <UserMenu loginButtonVariant={isDarkMode ? 'white' : 'black'} />
        </FlexRow>
      </nav>
    </header>
  )
}

export default TicketNav
